import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { selectCurrentProfileId } from '../../../../accounts/selectors/profile-id.selector.ts';
import { DefaultPathEnum } from '../../../../main/constants/default-path.ts';
import { LessonTaskStepKeys } from '../../../../main/constants/lesson-task-step-keys.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { LessonTaskStepKey } from '../../../../main/types/asset/lesson.types.ts';
import { getLessonsUtil } from '../../../../main/utils/asset/lessons.util.ts';
import { getSentryUtil } from '../../../../main/utils/monitoring/sentry.util.ts';
import { getUrlUtil } from '../../../../main/utils/url.util.ts';

import { ProgressErrors } from '../../../constants/errors.ts';
import { getLastCompletedStepQueryRequest } from '../../../requests/get-last-completed-step.query.ts';

import { setCurrentLessonTaskStepKey } from '../../lesson/lesson.slice.ts';
import { preloadLessonAssetsThunk } from '../../lesson/thunks/preload-lesson-assets.thunk.ts';

import { PROGRESS_SLICE_NAME, ProgressSliceState } from '../progress.slice.types.ts';

export const refreshLastCompletedStepThunk = createAsyncThunk<LessonTaskStepKey | undefined, void, ThunkApiConfigType>(
  `${PROGRESS_SLICE_NAME}/refreshLastCompletedStepThunk`,
  async (_, { getState, dispatch }): Promise<LessonTaskStepKey | undefined> => {
    const state = getState();

    const currentProfileId = selectCurrentProfileId(state);

    if (!currentProfileId) {
      getSentryUtil().trackError(new Error(ProgressErrors.NO_CURRENT_PROFILE), {});
      return undefined;
    }

    const response = await getLastCompletedStepQueryRequest({
      profileId: currentProfileId,
    });

    if (!response.success) {
      getSentryUtil().trackError(new Error(response.message), {});
      return undefined;
    }

    const { lesson, task, step } = response.data;

    const lastCompletedStep = {
      lesson: lesson ?? LessonTaskStepKeys.DEFAULT_LAST_COMPLETED.lesson,
      task: task ?? LessonTaskStepKeys.DEFAULT_LAST_COMPLETED.task,
      step: step ?? LessonTaskStepKeys.DEFAULT_LAST_COMPLETED.step,
    };

    const nextStepKey = getLessonsUtil().getNextStepKey(lastCompletedStep);
    await Promise.allSettled([
      dispatch(preloadLessonAssetsThunk({ lessonKey: nextStepKey.lesson })).unwrap(),
      dispatch(setCurrentLessonTaskStepKey(nextStepKey)),
    ]);
    getUrlUtil().navigateToUrl(DefaultPathEnum.LEARN_SCREEN);

    return lastCompletedStep;
  }
);

export const refreshLastCompletedStepThunkExtraReducers = (
  builder: ActionReducerMapBuilder<ProgressSliceState>
): ActionReducerMapBuilder<ProgressSliceState> =>
  builder.addCase(refreshLastCompletedStepThunk.fulfilled, (state, action) => {
    if (action.payload) {
      state.lastCompletedStep = action.payload;
    }
  });
